// Provider-aware embedding for KaziPeople Web (Phase 2, first slice).
//
// ⚠️ WHY A MAP AND NOT "JUST TRY AN IFRAME". A browser will not tell you why a
// frame failed: `X-Frame-Options` and `CSP: frame-ancestors` block silently, so
// an optimistic iframe gives a blank box with no way to detect it and fall
// back. Probing the headers needs a server round-trip (gateway, Phase 2b).
// Until then the honest thing is to embed only URLs whose provider PUBLISHES an
// embeddable form, and leave everything else on the existing external card —
// which is a real state, not a failure.
//
// ⚠️ THE CANONICAL URL IS USUALLY NOT EMBEDDABLE; a sibling is. youtube.com
// sends `X-Frame-Options: SAMEORIGIN` (verified), while youtube.com/embed/<id>
// carries no framing header at all. So this REWRITES rather than deciding
// yes/no about the address the user typed — and that is exactly why
// youtube.com's HOME page still opens externally: there is no embeddable form
// of a feed, only of a specific video.

export interface ProviderEmbed {
  /** The URL to put in the iframe — a rewrite, not the input. */
  embedUrl: string;
  /** Shown to the user so the embed is never anonymous. */
  provider: string;
  /** Video players need a 16:9 box; documents want the tall default. */
  aspect: 'video' | 'document';
}

const YT_ID = /^[\w-]{6,15}$/; // conservative: ids are short and URL-safe

/** host === domain, or a subdomain of it. */
const hostIs = (host: string, domain: string): boolean =>
  host === domain || host.endsWith('.' + domain);

function youtube(u: URL): ProviderEmbed | null {
  const host = u.hostname.toLowerCase().replace(/^www\./, '');
  let id: string | null = null;

  if (hostIs(host, 'youtu.be')) {
    id = u.pathname.slice(1).split('/')[0] || null;
  } else if (hostIs(host, 'youtube.com') || hostIs(host, 'youtube-nocookie.com')) {
    if (u.pathname === '/watch') id = u.searchParams.get('v');
    else if (u.pathname.startsWith('/embed/')) id = u.pathname.slice('/embed/'.length).split('/')[0];
    else if (u.pathname.startsWith('/shorts/')) id = u.pathname.slice('/shorts/'.length).split('/')[0];
    else if (u.pathname.startsWith('/live/')) id = u.pathname.slice('/live/'.length).split('/')[0];
  }
  if (!id || !YT_ID.test(id)) return null;

  // youtube-nocookie keeps the embed out of the viewer's ad profile; there is
  // no reason to use the tracking host for work content.
  const out = new URL(`https://www.youtube-nocookie.com/embed/${id}`);
  // Carry a start offset if the viewer had one, so the embed opens where they were.
  const t = u.searchParams.get('t') || u.searchParams.get('start');
  const secs = t ? parseInt(String(t).replace(/[^0-9]/g, ''), 10) : NaN;
  if (Number.isFinite(secs) && secs > 0) out.searchParams.set('start', String(secs));
  return { embedUrl: out.toString(), provider: 'YouTube', aspect: 'video' };
}

function vimeo(u: URL): ProviderEmbed | null {
  const host = u.hostname.toLowerCase().replace(/^www\./, '');
  if (hostIs(host, 'player.vimeo.com')) {
    return { embedUrl: u.toString(), provider: 'Vimeo', aspect: 'video' };
  }
  if (!hostIs(host, 'vimeo.com')) return null;
  const id = u.pathname.split('/').filter(Boolean)[0];
  if (!id || !/^\d+$/.test(id)) return null;
  return { embedUrl: `https://player.vimeo.com/video/${id}`, provider: 'Vimeo', aspect: 'video' };
}

function loom(u: URL): ProviderEmbed | null {
  const host = u.hostname.toLowerCase().replace(/^www\./, '');
  if (!hostIs(host, 'loom.com')) return null;
  const parts = u.pathname.split('/').filter(Boolean);
  if (parts[0] !== 'share' && parts[0] !== 'embed') return null;
  const id = parts[1];
  if (!id || !/^[0-9a-f]{16,}$/i.test(id)) return null;
  return { embedUrl: `https://www.loom.com/embed/${id}`, provider: 'Loom', aspect: 'video' };
}

const GOOGLE_DOC_KINDS: Record<string, string> = {
  document: 'Google Docs',
  spreadsheets: 'Google Sheets',
  presentation: 'Google Slides',
};

function googleDocs(u: URL): ProviderEmbed | null {
  const host = u.hostname.toLowerCase().replace(/^www\./, '');
  if (!hostIs(host, 'docs.google.com')) return null;
  const parts = u.pathname.split('/').filter(Boolean);
  const kind = parts[0];
  const label = GOOGLE_DOC_KINDS[kind];
  if (!label || parts[1] !== 'd') return null;
  const id = parts[2];
  if (!id) return null;
  // ⚠️ /preview, never /edit. An embedded editor would ask the viewer to sign
  // in inside a frame — which is the phishing shape KaziPeople Web exists to
  // avoid, and Google blocks framing of the editor anyway.
  const path = kind === 'presentation' ? 'embed' : 'preview';
  return {
    embedUrl: `https://docs.google.com/${kind}/d/${id}/${path}`,
    provider: label,
    aspect: 'document',
  };
}

const PROVIDERS: Array<(u: URL) => ProviderEmbed | null> = [youtube, vimeo, loom, googleDocs];

/**
 * The embeddable form of `url`, or null when no provider publishes one.
 *
 * ⚠️ Null is the common answer and is NOT a failure — it means the external
 * card, which is an honest state. Never guess an embed URL: a frame that
 * silently refuses to load is worse than a card that says where the link goes.
 */
export function providerEmbedFor(url: string): ProviderEmbed | null {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return null;
  }
  // Only ever embed over TLS — a mixed-content frame is blocked by the browser
  // and would present as the same silent blank box.
  if (u.protocol !== 'https:') return null;
  for (const p of PROVIDERS) {
    const hit = p(u);
    if (hit) return hit;
  }
  return null;
}

// ⚠️ "This site doesn't support embedded viewing" is MISLEADING for a provider
// we can in fact embed. Someone who pastes youtube.com gets told YouTube does
// not support embedding — which is false, and leaves them with nothing to try.
// YouTube does not publish an embeddable form of its FEED; it publishes one per
// VIDEO. The difference is the whole answer, and the generic card hides it.
//
// So: when a URL belongs to a provider we know, but no embeddable form could be
// derived from it, say which provider it is and what to paste instead.

interface ProviderHint {
  match: (host: string) => boolean;
  provider: string;
  /** What the person should paste instead, in their words not ours. */
  hint: string;
}

const PROVIDER_HINTS: ProviderHint[] = [
  {
    match: (h) => hostIs(h, 'youtube.com') || hostIs(h, 'youtu.be') || hostIs(h, 'youtube-nocookie.com'),
    provider: 'YouTube',
    hint: 'Paste a link to a specific video to watch it here.',
  },
  {
    match: (h) => hostIs(h, 'vimeo.com'),
    provider: 'Vimeo',
    hint: 'Paste a link to a specific video to watch it here.',
  },
  {
    match: (h) => hostIs(h, 'loom.com'),
    provider: 'Loom',
    hint: 'Paste a Loom share link (loom.com/share/…) to watch the recording here.',
  },
  {
    match: (h) => hostIs(h, 'docs.google.com'),
    provider: 'Google Docs',
    hint: 'Open a specific document, sheet or slide deck and paste that link to read it here.',
  },
];

/**
 * Guidance for a URL that belongs to a provider we support but cannot embed.
 *
 * Returns null when the URL embeds fine, or when we know nothing about the
 * site — in which case the generic "opens in your browser" card is honest, and
 * inventing advice would be worse than saying nothing.
 */
export function providerGuidanceFor(url: string): ProviderHint | null {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return null;
  }
  // If it embeds, there is nothing to advise.
  if (providerEmbedFor(url)) return null;
  const host = u.hostname.toLowerCase().replace(/^www\./, '');
  return PROVIDER_HINTS.find((p) => p.match(host)) ?? null;
}
